import { isEqual } from 'lodash-es'
import { APPSTATE, SETTINGSSTATE } from '.'
import type { WebcamPublisherPayload } from '../../types/mfc'
import type {
    AppState,
    MyWebcamState,
    MyWebcamStatePayload,
} from '../../types/state'
import { appAction } from '../actions/workerActions'
import { AppAction, AppStateType, SettingsType } from '../constants/enums'
import { BROADCASTING_STATE } from '../constants/myWebcam'
import { WREQUEST_ID_START } from '../mfcLogic/mfcConstants'
import {
    getVideoServerRequestURL,
    ModelDirectorResponse,
} from '../mfcLogic/videoPublisher'
import { controller } from '../utilities/abortController'
import { pubSubAppState, pubSubSettings } from './pubSub'
export { AppStateType, pubSubAppState }

export const setAppState = <T extends keyof AppState>(
    key: T,
    appState: AppState[T],
    immediate = true,
) => {
    if (!key) throw new Error('[setAppState] key is required')
    if (isEqual(APPSTATE[key], appState)) return
    APPSTATE[key] = appState
    pubSubAppState.emit(key, appState, immediate)
}

export const updateAppState = <T extends keyof AppState>(
    key: T,
    appState: Partial<AppState[T]> = {},
    immediate = true,
) => {
    if (!key) throw new Error('[updateAppState] key is required')
    setAppState(key, { ...APPSTATE[key], ...appState }, immediate)
}

export function sendPublishCam(payload: WebcamPublisherPayload) {
    appAction(AppAction.PUBLISH_WEBCAM, payload)
}

export function resumeSocketConnection() {
    appAction(AppAction.RESUME_SOCKET_CONNECTION, {})
}

export function pauseSocketConnection() {
    appAction(AppAction.PAUSE_SOCKET_CONNECTION, {})
}

export function notifyWorkerOfBrowserReload() {
    appAction(AppAction.BROWSER_RELOAD, {})
}

export function resetBroadcastingState() {
    updateAppState(AppStateType.MYWEBCAM, {
        broadcasting: BROADCASTING_STATE.UNINITIALIZED,
        pendingStop: false,
        settingsVisible: false,
    })
}

// asks FCS to publish; the publisher connects once it reports ready (connectPendingMyWebcam)
export async function startMyWebcam(id: number) {
    try {
        const res = await fetch(getVideoServerRequestURL(id), {
            signal: controller.signal,
        })
        const { videoserver: vidserv } =
            (await res.json()) as ModelDirectorResponse
        updateAppState(AppStateType.MYWEBCAM, { pendingServer: vidserv })
        sendPublishCam({ _reqid: id + WREQUEST_ID_START, vidserv, room: id })
    } catch (e) {
        if (process.env.NODE_ENV === 'development') {
            console.warn(`*** ERROR (fetchVideoServer)`, e)
        }
    }
}

export function connectPendingMyWebcam(
    publisher = APPSTATE.myWebcam.publisher,
) {
    const { broadcasting, pendingServer } = APPSTATE.myWebcam
    if (broadcasting !== BROADCASTING_STATE.OFF || !pendingServer) return
    updateAppState(AppStateType.MYWEBCAM, { pendingServer: null })
    publisher?.connect()
}

export function stopMyWebcam() {
    const { publisher, broadcasting } = APPSTATE.myWebcam
    publisher?.disconnect()
    updateAppState(AppStateType.MYWEBCAM, {
        pendingServer: null,
        pendingStop: true,
        settingsVisible: false,
    })
    // a publisher that never went live reports nothing more
    if (broadcasting !== BROADCASTING_STATE.ON) resetBroadcastingState()
}

export function setMyWebcamSettingsVisible(settingsVisible: boolean) {
    updateAppState(AppStateType.MYWEBCAM, { settingsVisible })
}

// a who-can-watch change restarts the broadcast on the server, so the publisher stops
const myWebcamOptions = {
    last: SETTINGSSTATE[SettingsType.MY_WEBCAM]?.options as number | undefined,
}
pubSubSettings.on(
    SettingsType.MY_WEBCAM,
    ({ options }: { options?: number }) => {
        const changed = myWebcamOptions.last !== options
        myWebcamOptions.last = options
        if (
            changed &&
            APPSTATE.myWebcam.broadcasting === BROADCASTING_STATE.ON
        ) {
            stopMyWebcam()
        }
    },
)

export function updateMyWebcamState(payload: MyWebcamStatePayload) {
    if (typeof payload.broadcasting === 'object') {
        const { state: broadcastingState } = payload.broadcasting
        const broadcasting =
            broadcastingState === 0
                ? BROADCASTING_STATE.ON
                : BROADCASTING_STATE.OFF
        updateAppState(AppStateType.MYWEBCAM, { broadcasting })
        if (
            broadcasting === BROADCASTING_STATE.OFF &&
            APPSTATE.myWebcam.pendingStop
        ) {
            resetBroadcastingState()
        }
        return
    } else if (payload.isVideoPublisherReady) {
        updateAppState(AppStateType.MYWEBCAM, {
            ...payload,
            broadcasting: BROADCASTING_STATE.OFF,
        })
        connectPendingMyWebcam(payload.publisher)
        return
    } else if (payload.error) {
        const { error } = payload
        updateAppState(AppStateType.MYWEBCAM, {
            error,
            broadcasting: BROADCASTING_STATE.ERROR,
        })
        return
    }
    const myWebcamState: Omit<MyWebcamState, 'broadcasting'> = payload
    updateAppState(AppStateType.MYWEBCAM, myWebcamState)
}
